import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import classNames from 'classnames';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';

import intlShape from '@/shared/utils/intlShape';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import { emptyGuid } from '@/shared/utils/guid';
import { MainTaskIcon, SubTaskIcon } from '@/shared/components/Icons';
import messages from '../hrmMessages';

const TOTAL_ROW_ID = '00000000-0000-0000-0000-000000000001';
const SORT_STORAGE_KEY = 'CapacityPlanningReportSort';

const applySorting = (rows, sortKey, sortDesc, hideableColumns) => {
  if (!rows || rows.length === 0) return rows;

  const sign = sortDesc ? -1 : 1;
  let ordering = (a, b) => 0;

  if (sortKey === 'employee') {
    ordering = (a, b) => sign * a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeRole') {
    ordering = (a, b) =>
      sign * a.employeeRole.localeCompare(b.employeeRole) || a.employee.localeCompare(b.employee);
  } else if (sortKey === 'total') {
    ordering = (a, b) => sign * (a.total - b.total) || a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeRemainingCapacity') {
    ordering = (a, b) =>
      sign * (a.employeeRemainingCapacity - b.employeeRemainingCapacity) ||
      a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeTotalCapacity') {
    ordering = (a, b) =>
      sign * (a.employeeTotalCapacity - b.employeeTotalCapacity) ||
      a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeContractCapacity') {
    ordering = (a, b) =>
      sign * ((a.employeeWeeklyHoursContract || 0) - (b.employeeWeeklyHoursContract || 0)) ||
      a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeWeeklyCapacity') {
    ordering = (a, b) =>
      sign * (a.employeeWeeklyHours - b.employeeWeeklyHours) ||
      a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeYearlyCapacity') {
    ordering = (a, b) =>
      sign * ((a.employeeYearlyCapacity || 0) - (b.employeeYearlyCapacity || 0)) ||
      a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeAvailableCapacity') {
    ordering = (a, b) =>
      sign *
        (a.employeeAvailableHours -
          (a.totalScheduledMinutes || a.total) -
          (b.employeeAvailableHours - (b.totalScheduledMinutes || b.total))) ||
      a.employee.localeCompare(b.employee);
  } else if (sortKey === 'occupancy') {
    const getOccupancy = row => {
      const remaining = row.employeeRemainingCapacity || 0;
      if (remaining <= 0) return 0;
      const available = row.employeeAvailableHours - (row.totalScheduledMinutes || row.total);
      return (remaining - available) / remaining;
    };
    ordering = (a, b) =>
      sign * (getOccupancy(a) - getOccupancy(b)) || a.employee.localeCompare(b.employee);
  } else if (sortKey !== '') {
    const column = hideableColumns.findIndex(col => col.id === sortKey);
    if (column >= 0) {
      ordering = (a, b) =>
        sign * ((a.counts[column] || 0) - (b.counts[column] || 0)) ||
        a.employee.localeCompare(b.employee);
    }
  }

  return [...rows].sort((a, b) => {
    if (a.id === TOTAL_ROW_ID) return 1;
    if (b.id === TOTAL_ROW_ID) return -1;
    if (a.id === emptyGuid) return 1;
    if (b.id === emptyGuid) return -1;
    return ordering(a, b);
  });
};

const CapacityPlanningReportTable = ({
  data,
  hideableColumns,
  columns,
  filters,
  loading,
  capacityColumnStatus,
  intl,
}) => {
  const show = {
    employeeRole: capacityColumnStatus.employeeRole || false,
    fullWeeklyCapacity: capacityColumnStatus.fullWeeklyCapacity || false,
    weeklyCapacity: capacityColumnStatus.weeklyCapacity !== false,
    yearlyCapacity: capacityColumnStatus.yearlyCapacity || false,
    totalCapacity: capacityColumnStatus.totalCapacity || false,
    remainingCapacity: capacityColumnStatus.remainingCapacity !== false,
    availableCapacity: capacityColumnStatus.availableCapacity !== false,
  };
  const capacityColCount = [
    show.fullWeeklyCapacity,
    show.weeklyCapacity,
    show.yearlyCapacity,
    show.totalCapacity,
    show.remainingCapacity,
    show.availableCapacity,
  ].filter(Boolean).length;
  const employeeColSpan = 1 + (show.employeeRole ? 1 : 0);

  const [sortKey, setSortKey] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(SORT_STORAGE_KEY));
      return saved?.sortKey ?? 'employee';
    } catch {
      return 'employee';
    }
  });
  const [sortDesc, setSortDesc] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(SORT_STORAGE_KEY));
      return saved?.sortDesc ?? false;
    } catch {
      return false;
    }
  });

  const handleSort = key => {
    const newDesc = key === sortKey ? !sortDesc : false;
    const newKey = key;
    setSortKey(newKey);
    setSortDesc(newDesc);
    sessionStorage.setItem(
      SORT_STORAGE_KEY,
      JSON.stringify({ sortKey: newKey, sortDesc: newDesc }),
    );
  };

  const sortedData = useMemo(() => applySorting(data, sortKey, sortDesc, hideableColumns), [
    data,
    sortKey,
    sortDesc,
    hideableColumns,
  ]);

  const groupBy = filters.groupBy ?? 0;
  const isGrouped = groupBy > 0;
  const showPercentage = isGrouped && filters.displayMode === 0 && !filters.mainTaskTemplateId;
  const showOccupancy = filters.displayMode === 0 && !filters.mainTaskTemplateId;

  const getOccupancyPct = row => {
    const remaining = row.employeeRemainingCapacity || 0;
    if (remaining <= 0) return null;
    const available = row.employeeAvailableHours - (row.totalScheduledMinutes || row.total);
    return Math.round(((remaining - available) / remaining) * 100);
  };

  const getOccupancyClass = pct => {
    if (pct === null) return null;
    if (pct > 100) return 'capacity-pct-red';
    if (pct >= 80) return 'capacity-pct-yellow';
    return 'capacity-pct-green';
  };

  const visibleColumnCount = columns.length;
  const getTasksGroupLabel = () => {
    if (groupBy === 2) return intl.formatMessage(messages.CapacityQuarterOverview);
    if (groupBy === 1) return intl.formatMessage(messages.CapacityMonthOverview);
    if (filters.displayMode === 1) return intl.formatMessage(messages.CapacityTasksCount);
    if (filters.displayMode === 2) return intl.formatMessage(messages.CapacityTasksRelations);
    return intl.formatMessage(messages.CapacityTasks);
  };
  const dynamicGroupLabel = getTasksGroupLabel();

  const hours = intl.formatMessage(messages.HoursAbbreviated);

  const renderCapacityValue = minutes => {
    if (!minutes || minutes === 0) return '-';
    const sign = minutes < 0 ? '-' : '';
    const absMinutes = Math.round(Math.abs(minutes));
    const hrs = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    const hrsFormatted = intl.formatNumber(hrs);
    return mins === 0
      ? `${sign}${hrsFormatted}${hours}`
      : `${sign}${hrsFormatted}${hours}${String(mins).padStart(2, '0')}`;
  };

  const renderValue = x => {
    if (filters.displayMode === 1 || filters.displayMode === 2) {
      return x === 0 ? '-' : intl.formatNumber(x);
    }
    return renderCapacityValue(x);
  };

  const getCapacityPercentageClass = (scheduledMinutes, remainingCapacityMinutes) => {
    if (remainingCapacityMinutes === undefined || remainingCapacityMinutes === null) return null;
    if (remainingCapacityMinutes <= 0) {
      return scheduledMinutes > 0 ? 'capacity-pct-red' : 'capacity-pct-green';
    }
    const pct = (scheduledMinutes / remainingCapacityMinutes) * 100;
    if (pct > 100) return 'capacity-pct-red';
    if (pct >= 80) return 'capacity-pct-yellow';
    return 'capacity-pct-green';
  };

  const formatCapacityPercentage = (scheduledMinutes, remainingCapacityMinutes) => {
    if (remainingCapacityMinutes === undefined || remainingCapacityMinutes === null) return null;
    if (remainingCapacityMinutes <= 0) return null;
    const pct = Math.round((scheduledMinutes / remainingCapacityMinutes) * 100);
    return `${pct}%`;
  };

  const capacityColSpan = capacityColCount;
  const firstCapacityCol = [
    'fullWeeklyCapacity',
    'weeklyCapacity',
    'yearlyCapacity',
    'totalCapacity',
    'remainingCapacity',
    'availableCapacity',
  ].find(key => show[key]);

  const renderContractCapacity = row => {
    const contractValue = renderCapacityValue(row.employeeWeeklyHoursContract);
    if (contractValue === '-') return '-';
    if (row.productivityPercentage == null) return contractValue;
    return `${contractValue} (${Number(row.productivityPercentage)}%)`;
  };

  const renderSortableHeader = (title, key, style, extraClassName, taskType, tooltip) => {
    const isActive = key === sortKey;
    const sortIcon = isActive
      ? classNames('fa margin10R', {
          'fa-caret-down': sortDesc,
          'fa-caret-up': !sortDesc,
        })
      : null;

    return (
      <th className={classNames(extraClassName)} style={style} key={key}>
        <span className="clickable" onClick={() => handleSort(key)}>
          {sortIcon && <i className={sortIcon} />}
          {taskType === 'mainTask' && <MainTaskIcon />}
          {taskType === 'subTask' && <SubTaskIcon />}
          {title}
        </span>
        {tooltip && (
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="bottom"
            rootClose
            overlay={<Popover id={`popover-${key}`}>{tooltip}</Popover>}
          >
            <span className="explanation-icon margin5L">
              <FaInfoCircle />
            </span>
          </OverlayTrigger>
        )}
      </th>
    );
  };

  const renderRow = row => {
    const isUnassigned = row.id === emptyGuid;
    const isTotals = !row.employee && row.id !== emptyGuid;
    const header = row.employee
      ? row.employee
      : isTotals
      ? intl.formatMessage(messages.Totals)
      : intl.formatMessage(messages.Unassigned);

    const rowClassName = classNames({
      'unassigned-row': isUnassigned,
      'totals-row': isTotals,
    });

    return (
      <tbody className="hover" key={'row_' + row.id}>
        <tr className={rowClassName || undefined}>
          <td className={classNames('sticky-col', { 'removed-user': !row.isActive })}>{header}</td>
          {show.employeeRole && <td>{row.employeeRole}</td>}
          {show.fullWeeklyCapacity && (
            <td className={firstCapacityCol === 'fullWeeklyCapacity' ? 'border-group' : undefined}>
              {renderContractCapacity(row)}
            </td>
          )}
          {show.weeklyCapacity && (
            <td className={firstCapacityCol === 'weeklyCapacity' ? 'border-group' : undefined}>
              {renderCapacityValue(row.employeeWeeklyHours)}
            </td>
          )}
          {show.yearlyCapacity && (
            <td className={firstCapacityCol === 'yearlyCapacity' ? 'border-group' : undefined}>
              {renderCapacityValue(row.employeeYearlyCapacity)}
            </td>
          )}
          {show.totalCapacity && (
            <td className={firstCapacityCol === 'totalCapacity' ? 'border-group' : undefined}>
              {renderCapacityValue(row.employeeTotalCapacity)}
            </td>
          )}
          {show.remainingCapacity && (
            <td className={firstCapacityCol === 'remainingCapacity' ? 'border-group' : undefined}>
              {renderCapacityValue(row.employeeRemainingCapacity)}
            </td>
          )}
          {show.availableCapacity && (
            <td className={firstCapacityCol === 'availableCapacity' ? 'border-group' : undefined}>
              {renderCapacityValue(
                row.employeeAvailableHours - (row.totalScheduledMinutes || row.total),
              )}
            </td>
          )}
          {showOccupancy &&
            (() => {
              const isSpecialRow = isTotals || isUnassigned;
              const pct = !isSpecialRow ? getOccupancyPct(row) : null;
              const pctClass = getOccupancyClass(pct);
              const renderOccupancyCell = (cellPct, cellClass) => (
                <td className={classNames('border-group', { [cellClass]: !!cellClass })}>
                  {cellPct !== null ? (
                    <div className="occupancy-cell">
                      <span>{cellPct}%</span>
                      <div className="occupancy-bar-track">
                        <div
                          className={classNames('occupancy-bar-fill', cellClass)}
                          style={{ width: `${Math.min(cellPct, 100)}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    '-'
                  )}
                </td>
              );
              if (isTotals) {
                const totalRemaining = data
                  .filter(r => r.id !== TOTAL_ROW_ID && r.id !== emptyGuid && r.isActive)
                  .reduce((sum, r) => sum + (r.employeeRemainingCapacity || 0), 0);
                const totalAvailable = data
                  .filter(r => r.id !== TOTAL_ROW_ID && r.id !== emptyGuid && r.isActive)
                  .reduce(
                    (sum, r) =>
                      sum + (r.employeeAvailableHours - (r.totalScheduledMinutes || r.total)),
                    0,
                  );
                const totalPct =
                  totalRemaining > 0
                    ? Math.round(((totalRemaining - totalAvailable) / totalRemaining) * 100)
                    : null;
                return renderOccupancyCell(totalPct, getOccupancyClass(totalPct));
              }
              return renderOccupancyCell(pct, pctClass);
            })()}
          {columns.map((column, idx) => {
            const value = row.counts[column] || 0;
            const col = hideableColumns[column];
            const periodKey = col?.id;
            const remainingCap = row.periodRemainingCapacity?.[periodKey];
            const showCellPct = showPercentage && !isTotals && !isUnassigned;
            const pctClass = showCellPct ? getCapacityPercentageClass(value, remainingCap) : null;
            const pctLabel = showCellPct ? formatCapacityPercentage(value, remainingCap) : null;

            return (
              <td
                key={row.id + '_' + column}
                className={classNames({ 'border-group': idx === 0, [pctClass]: !!pctClass })}
              >
                <span>
                  {renderValue(value)}
                  {pctLabel && <span className="capacity-pct-label"> ({pctLabel})</span>}
                </span>
              </td>
            );
          })}
          <td className="border-group">{renderValue(row.total)}</td>
        </tr>
      </tbody>
    );
  };

  const noData = !loading && (!sortedData || sortedData.length === 0);
  const totalColCount =
    employeeColSpan + capacityColCount + (showOccupancy ? 1 : 0) + visibleColumnCount + 1;

  return (
    <div className="capacity-report-table-container">
      <div className="capacity-report-table-wrapper">
        <table className="table table-report capacity-report-table scrollable-table">
          <thead>
            <tr>
              <th colSpan={employeeColSpan} className="text-center sticky-col">
                <FormattedMessage {...messages.Employee} />
              </th>
              {capacityColSpan > 0 && (
                <th colSpan={capacityColSpan} className="text-center border-group">
                  <FormattedMessage {...messages.CapacityGroupHeader} />
                </th>
              )}
              {showOccupancy && (
                <th className="text-center border-group">
                  <FormattedMessage {...messages.CapacityOccupancyHeader} />
                </th>
              )}
              {visibleColumnCount > 0 && (
                <th colSpan={visibleColumnCount} className="text-center border-group">
                  {dynamicGroupLabel}
                </th>
              )}
              <th className="text-center border-group">
                <FormattedMessage {...messages.Totals} />
              </th>
            </tr>
            <tr>
              {renderSortableHeader(
                intl.formatMessage(messages.Employee),
                'employee',
                {
                  width: '200px',
                },
                'sticky-col',
              )}
              {show.employeeRole &&
                renderSortableHeader(intl.formatMessage(messages.EmployeeRole), 'employeeRole', {
                  width: '200px',
                })}
              {show.fullWeeklyCapacity &&
                renderSortableHeader(
                  intl.formatMessage(messages.EmployeeFullWeeklyCapacity),
                  'employeeContractCapacity',
                  { width: '200px' },
                  firstCapacityCol === 'fullWeeklyCapacity' ? 'border-group' : undefined,
                )}
              {show.weeklyCapacity &&
                renderSortableHeader(
                  intl.formatMessage(messages.EmployeeWeeklyCapacity),
                  'employeeWeeklyCapacity',
                  { width: '200px' },
                  firstCapacityCol === 'weeklyCapacity' ? 'border-group' : undefined,
                )}
              {show.yearlyCapacity &&
                renderSortableHeader(
                  intl.formatMessage(messages.EmployeeYearlyCapacity),
                  'employeeYearlyCapacity',
                  { width: '200px' },
                  firstCapacityCol === 'yearlyCapacity' ? 'border-group' : undefined,
                  undefined,
                  intl.formatMessage(messages.EmployeeYearlyCapacityTooltip),
                )}
              {show.totalCapacity &&
                renderSortableHeader(
                  intl.formatMessage(messages.EmployeeTotalCapacity),
                  'employeeTotalCapacity',
                  { width: '200px' },
                  firstCapacityCol === 'totalCapacity' ? 'border-group' : undefined,
                  undefined,
                  intl.formatMessage(messages.EmployeeTotalCapacityTooltip),
                )}
              {show.remainingCapacity &&
                renderSortableHeader(
                  intl.formatMessage(messages.EmployeeRemainingCapacity),
                  'employeeRemainingCapacity',
                  { width: '200px' },
                  firstCapacityCol === 'remainingCapacity' ? 'border-group' : undefined,
                  undefined,
                  intl.formatMessage(messages.EmployeeRemainingCapacityTooltip),
                )}
              {show.availableCapacity &&
                renderSortableHeader(
                  intl.formatMessage(messages.EmployeeAvailableCapacity),
                  'employeeAvailableCapacity',
                  { width: '200px' },
                  firstCapacityCol === 'availableCapacity' ? 'border-group' : undefined,
                  undefined,
                  intl.formatMessage(messages.EmployeeAvailableCapacityTooltip),
                )}
              {showOccupancy &&
                renderSortableHeader(
                  intl.formatMessage(messages.CapacityOccupancy),
                  'occupancy',
                  { width: '150px' },
                  'border-group',
                  undefined,
                  intl.formatMessage(messages.CapacityOccupancyTooltip),
                )}
              {hideableColumns &&
                hideableColumns.map((col, index) =>
                  columns.includes(index)
                    ? renderSortableHeader(
                        col.id === emptyGuid
                          ? intl.formatMessage(messages.WithoutTemplate)
                          : col.label,
                        col.id,
                        { width: '200px' },
                        index === columns[0] ? 'border-group' : undefined,
                        filters.mainTaskTemplateId && !isGrouped
                          ? col.id === filters.mainTaskTemplateId
                            ? 'mainTask'
                            : 'subTask'
                          : undefined,
                      )
                    : null,
                )}
              {renderSortableHeader(
                intl.formatMessage(messages.Totals),
                'total',
                undefined,
                'border-group',
              )}
            </tr>
          </thead>
          {noData && (
            <tbody>
              <tr className="no-data-row">
                <td colSpan={totalColCount}>
                  <FormattedMessage {...messages.NoEmployeesFound} />
                </td>
              </tr>
            </tbody>
          )}
          {!loading &&
            sortedData &&
            sortedData.length > 0 &&
            sortedData.map(item => renderRow(item))}
        </table>
        {loading && (
          <div>
            <LoadingAnimation />
          </div>
        )}
      </div>
    </div>
  );
};

CapacityPlanningReportTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hideableColumns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      selected: PropTypes.bool,
    }),
  ).isRequired,
  columns: PropTypes.arrayOf(PropTypes.number).isRequired,
  filters: PropTypes.shape({
    displayMode: PropTypes.number,
    mainTaskTemplateId: PropTypes.string,
    groupBy: PropTypes.number,
  }).isRequired,
  loading: PropTypes.bool,
  capacityColumnStatus: PropTypes.object,
  intl: intlShape.isRequired,
};

CapacityPlanningReportTable.defaultProps = {
  loading: false,
  capacityColumnStatus: {},
};

export default injectIntl(CapacityPlanningReportTable);
